import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const api = axios.create({
  baseURL: `${BACKEND_URL}/api`,
  withCredentials: true,
  timeout: 30000,
});

// Cafe Owner branch context for menu / bulk-menu / add-on calls made from the Website Orders workspace.
// The backend honours this header for cafe_owner only (branch must belong to the owner's cafe).
let _ownerBranchId = null;
export const setOwnerBranchHeader = (id) => { _ownerBranchId = id || null; };
api.interceptors.request.use((cfg) => { if (_ownerBranchId) cfg.headers["X-Branch-Id"] = _ownerBranchId; return cfg; });

const _PROXY_LEAK_RE = /cloudflare|origin web server|bad\s*gateway|incomplete response|error code\s*5\d\d/i;
const _PROXY_MASK_MSG = "Service temporarily unavailable — please try again in a moment.";

// The ingress proxy sometimes substitutes a Cloudflare HTML error page for
// gateway failures — never surface that raw text/HTML in a toast.
function _maskProxyLeak(s) {
  if (typeof s !== "string" || !s) return s;
  const t = s.trim();
  if (t.startsWith("<") || _PROXY_LEAK_RE.test(t)) return _PROXY_MASK_MSG;
  return s;
}

export function formatApiError(detail) {
  // Handle Axios error objects directly — auto-extract status + endpoint.
  if (detail && typeof detail === "object" && detail.isAxiosError) {
    const status = detail.response?.status;
    const method = (detail.config?.method || "").toUpperCase();
    const url = detail.config?.url || "";
    // Gateway statuses are proxy-generated (Cloudflare HTML body) — mask.
    if (status === 502 || status === 503 || status === 504) return _PROXY_MASK_MSG;
    const data = detail.response?.data;
    if (typeof data === "string" && data.trim()) return _maskProxyLeak(data);
    const inner = data?.detail;
    const innerMsg = typeof inner === "string" ? inner
      : (inner && typeof inner === "object" && typeof inner.message === "string") ? inner.message
      : "";
    if (innerMsg) return _maskProxyLeak(innerMsg);
    if (status) return `${method || "GET"} ${url} → HTTP ${status}`;
    return detail.message || "Network error — check your connection.";
  }
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return _maskProxyLeak(detail);
  if (Array.isArray(detail)) return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail === "object") {
    // Backend commonly returns { code, message } — surface the human string.
    if (typeof detail.message === "string" && detail.message) return _maskProxyLeak(detail.message);
    if (typeof detail.msg === "string" && detail.msg) return _maskProxyLeak(detail.msg);
    if (typeof detail.detail === "string" && detail.detail) return _maskProxyLeak(detail.detail);
    if (typeof detail.error === "string" && detail.error) return _maskProxyLeak(detail.error);
    // Last resort — dump JSON instead of "[object Object]".
    try { return JSON.stringify(detail); } catch { return "Unknown error"; }
  }
  return String(detail);
}

export const fmtINR = (n) => {
  if (n === null || n === undefined || isNaN(n)) return "₹0";
  return "₹" + Number(n).toLocaleString("en-IN", { maximumFractionDigits: 2 });
};

export const fmtDate = (d) => {
  if (!d) return "—";
  try { return new Date(d).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" }); }
  catch { return String(d); }
};

export const fmtDay = (d) => {
  if (!d) return "—";
  try { return new Date(d).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }); }
  catch { return String(d); }
};
